// View.jsx – Batched fetching with progressive rendering
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import { expandRecurrences } from './recurrenceHelpers';
import './events.css';

/* ---------- helpers ---------- */
const ensureLeadingSlash = (p) => (p ? (p.startsWith('/') ? p : `/${p}`) : '');

const rawToDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

export const startDateOf = (item) =>
  rawToDate(item.start || item.start_date || item.effective || item.Date) ||
  rawToDate(item.end || item.end_date || item.expires || item.ExpirationDate);

export const endDateOf = (item) =>
  rawToDate(item.end || item.end_date || item.expires || item.ExpirationDate);

export const isUpcomingOrOngoing = (item, now = new Date()) => {
  const s = startDateOf(item);
  const e = endDateOf(item);
  const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (e) return e >= yesterdayStart;
  if (s) return s >= yesterdayStart;
  return false;
};

export const toEvent = (item, data = {}) => {
  let title = item.title;
  if (item._seqTotal > 1) {
    title = `${item.title} ${item._seqIndex}/${item._seqTotal}`;
  }
  
  let recurrenceData = null;
  let occurrencesData = null;

  if (item.recurrence) {
    if (typeof item.recurrence === 'string') {
      recurrenceData = item.recurrence;
    } else if (typeof item.recurrence === 'object') {
      recurrenceData =
        item.recurrence.rrule ||
        item.recurrence.RRULE ||
        item.recurrence.rule ||
        item.recurrence;
      occurrencesData =
        item.recurrence.occurrences ||
        item.recurrence.recurrence_occurrences ||
        null;
    }
  }

  recurrenceData =
    recurrenceData ||
    item.RRULE ||
    item.rrule ||
    item.recurrence_rule ||
    item.recurrenceRule ||
    null;

  occurrencesData =
    occurrencesData ||
    item.occurrences ||
    item.recurrence_occurrences ||
    item.recurrenceOccurrences ||
    null;
  
  return {
    id: item['@id'] || item.id,
    title: title,
    description: item.description || item.Description,
    start: item.start || item.start_date || item.effective || item.Date,
    end: item.end || item.end_date || item.expires || item.ExpirationDate,
    location: item.location || item.place || item.city || '',
    url: item.url || item['@id'] || '#',
    image: item.image || null,
    image_field: item.image_field || 'image',
    image_scales: item.image_scales || null,
    tags: Array.isArray(item.Subject) ? item.Subject : [],
    contactPerson: item.contact_name || item.contact_person || item.contactPerson || item.contact || '',
    buttonText: data.buttonText || 'View event',
    _recurrence: recurrenceData,
    _occurrences: occurrencesData,
    _seqIndex: item._seqIndex || 1,
    _seqTotal: item._seqTotal || 1,
  };
};

export const byStartAsc = (a, b) => {
  const sa = startDateOf(a);
  const sb = startDateOf(b);
  if (sa && sb) return sa - sb;
  if (sa) return -1;
  if (sb) return 1;
  return 0;
};
export const byStartDesc = (a, b) => -byStartAsc(a, b);

export const formatDay = (d) =>
  d.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

const sameDay = (a, b) => !!a && !!b && a.toDateString() === b.toDateString();

export const formatListWhen = (start, end) => {
  const s = rawToDate(start);
  const e = rawToDate(end);
  if (!s && !e) return '';

  const dateFmt = (d) =>
    d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  if (s && e) {
    const same = sameDay(s, e);
    if (same) return dateFmt(s);
    return `${dateFmt(s)} - ${dateFmt(e)}`;
  }

  if (s && !e) return dateFmt(s);
  if (!s && e) return dateFmt(e);
  return '';
};

export const formatAgendaWhen = (start, end) => {
  const s = rawToDate(start);
  const e = rawToDate(end);
  if (!s && !e) return '';

  const oneDay = !!(s && e && sameDay(s, e));
  if (oneDay) return '';
  
  if (s && e && !oneDay) {
    const ed = e.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return `Ends on ${ed}`;
  }
  
  if (s && !e) return '';
  
  if (!s && e) {
    const ed = e.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return `Ends on ${ed}`;
  }
  
  return '';
};

/* ---------- presentational subcomponents ---------- */
const TagList = ({ tags = [] }) => {
  if (!tags || !tags.length) return null;
  return (
    <ul className="agenda-tags">
      {tags.map((t, i) => {
        const href = `/search?Subject=${encodeURIComponent(t)}`;
        return (
          <li key={i}>
            <a
              className="agenda-tag"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {t}
            </a>
          </li>
        );
      })}
    </ul>
  );
};

const previewSrc = (ev) => {
  if (!ev?.id) return null;
  return `${ev.id}/@@images/preview_image`;
};

/* ---------- List ---------- */
const List = ({ events, data }) => (
  <ul className={`events-list ${data?.showImage !== false ? 'with-image' : 'no-image'}`}>
    {events.map((ev) => (
      <li key={`${ev.id}-${ev._seqIndex}`} className="events-list-item">
        {data?.showImage !== false ? (
          <a href={ev.url} className="events-thumb">
            <img src={previewSrc(ev)} alt="" loading="lazy" decoding="async" style={{ objectFit: 'cover', display: 'block' }} />
          </a>
        ) : null}
        <div className="events-body">
          <h3 className="events-title">
            <a href={ev.url}>{ev.title}</a>
          </h3>
          {data.showDate && (ev.start || ev.end) && (
            <div className="events-when">{formatListWhen(ev.start, ev.end)}</div>
          )}
          {data.showLocation && ev.location && (
            <div className="events-location">{ev.location}</div>
          )}
          {data.showTags && ev.tags?.length ? <TagList tags={ev.tags} /> : null}
          {data.showContactPerson && ev.contactPerson ? (
            <div className="events-contact">Contact: {ev.contactPerson}</div>
          ) : null}
          {data.showDescription && ev.description && (
            <p className="events-desc">{ev.description}</p>
          )}
          <p className="events-cta">
            <a className="events-button" href={ev.url}>
              {ev.buttonText}
            </a>
          </p>
        </div>
      </li>
    ))}
  </ul>
);

/* ---------- shared Agenda item ---------- */
const AgendaItem = ({ ev, data, index = 0 }) => {
  const line = formatAgendaWhen(ev.start, ev.end);
  const parity = index % 2 === 0 ? 'even' : 'odd';
  return (
    <li className={`agenda-item ${parity}`}>
      {data?.showImage !== false ? (
        <div className="agenda-image">
          <a href={ev.url}>
            <img src={previewSrc(ev)} alt="" />
          </a>
        </div>
      ) : null}
      <div className="agenda-body">
        <h4 className="agenda-title">
          <a href={ev.url}>{ev.title}</a>
        </h4>
        {data?.showContactPerson !== false && ev.contactPerson ? (
          <div className="agenda-contact">{ev.contactPerson}</div>
        ) : null}
        {line ? <div className="agenda-when">{line}</div> : null}
        {ev.location ? <div className="agenda-location">{ev.location}</div> : null}
        {data?.showTags !== false && ev.tags?.length ? <TagList tags={ev.tags} /> : null}
        {ev.description ? <p className="agenda-desc">{ev.description}</p> : null}
      </div>
    </li>
  );
};

/* ---------- Agenda (by day) ---------- */
const Agenda = ({ events, data }) => {
  const groups = events.reduce((acc, ev) => {
    const s = startDateOf(ev);
    if (!s) return acc;
    const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}`;
    acc[key] = acc[key] || { date: s, items: [] };
    acc[key].items.push(ev);
    return acc;
  }, {});
  const dayKeys = Object.keys(groups).sort((a, b) => (a < b ? -1 : 1));
  return (
    <div className="events-agenda">
      {dayKeys.map((k) => {
        const { date, items } = groups[k];
        const sorted = items.sort(byStartAsc);
        return (
          <section key={k} className="agenda-day">
            <h4 className="agenda-heading">{formatDay(date)}</h4>
            <ul className="agenda-list">
              {sorted.map((ev, idx) => (
                <AgendaItem key={`${ev.id}-${ev._seqIndex}`} ev={ev} data={data} index={idx} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

/* ---------- All events with optional year accordion ---------- */
const AllEvents = ({ events, accordion, pageSize = 0, data }) => {
  const [page, setPage] = useState(0);
  const eventsDesc = useMemo(() => [...events].sort(byStartDesc), [events]);
  const total = eventsDesc.length;
  const size = parseInt(pageSize, 10) || 0;
  const totalPages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1;

  useEffect(() => {
    setPage(0);
  }, [size, total]);

  const slice = useMemo(() => {
    if (size <= 0) return eventsDesc;
    const start = page * size;
    return eventsDesc.slice(start, start + size);
  }, [eventsDesc, page, size]);

  const Pager = () => {
    if (size <= 0 || totalPages <= 1) return null;
    return (
      <nav className="events-pager" aria-label="Events pagination">
        <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
          Previous
        </button>
        <span className="events-pager-status">Page {page + 1} of {totalPages}</span>
        <button type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
          Next
        </button>
      </nav>
    );
  };

  if (!accordion) {
    const groups = slice.reduce((acc, ev) => {
      const s = startDateOf(ev);
      if (!s) return acc;
      const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}`;
      acc[key] = acc[key] || { date: s, items: [] };
      acc[key].items.push(ev);
      return acc;
    }, {});
    const dayKeys = Object.keys(groups).sort((a, b) => (a > b ? -1 : 1));
    return (
      <>
        <div className="events-agenda">
          {dayKeys.map((k) => {
            const { date, items } = groups[k];
            const sorted = items.sort(byStartDesc);
            return (
              <section key={k} className="agenda-day">
                <h4 className="agenda-heading">{formatDay(date)}</h4>
                <ul className="agenda-list">
                  {sorted.map((ev, idx) => (
                    <AgendaItem key={`${ev.id}-${ev._seqIndex}`} ev={ev} data={data} index={idx} />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
        <Pager />
      </>
    );
  }

  const byYear = slice.reduce((acc, ev) => {
    const s = startDateOf(ev);
    const y = s ? s.getFullYear() : 'Unknown';
    acc[y] = acc[y] || [];
    acc[y].push(ev);
    return acc;
  }, {});
  const years = Object.keys(byYear).sort((a, b) => (parseInt(a) || 0) > (parseInt(b) || 0) ? -1 : 1);

  return (
    <>
      <div className="eea-accordion" data-accordion-group="events-by-year">
        {years.map((year, idx) => {
          const perYear = byYear[year];
          const groups = perYear.reduce((acc, ev) => {
            const s = startDateOf(ev);
            if (!s) return acc;
            const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}`;
            acc[key] = acc[key] || { date: s, items: [] };
            acc[key].items.push(ev);
            return acc;
          }, {});
          const dayKeys = Object.keys(groups).sort((a, b) => (a > b ? -1 : 1));
          const isOpen = idx === 0;

          return (
            <section key={year} className={`eea-accordion__item ${isOpen ? 'is-open' : ''}`}>
              <h3 className="eea-accordion__header">
                <button
                  id={`acc-trigger-${year}`}
                  className="eea-accordion__trigger"
                  aria-expanded={isOpen}
                  aria-controls={`acc-panel-${year}`}
                  onClick={(e) => {
                    const panel = document.getElementById(`acc-panel-${year}`);
                    const expanded = e.currentTarget.getAttribute('aria-expanded') === 'true';
                    e.currentTarget.setAttribute('aria-expanded', String(!expanded));
                    if (panel) panel.hidden = expanded;
                    e.currentTarget.closest('.eea-accordion__item')?.classList.toggle('is-open');
                  }}
                  type="button"
                >
                  <span className="eea-accordion__title">
                    {year} <span className="eea-accordion__count" aria-hidden="true">({perYear.length})</span>
                  </span>
                  <span className="eea-accordion__chevron" aria-hidden="true" />
                </button>
              </h3>

              <div
                id={`acc-panel-${year}`}
                className="eea-accordion__panel"
                role="region"
                aria-labelledby={`acc-trigger-${year}`}
                hidden={!isOpen}
              >
                {dayKeys.map((k) => {
                  const { date, items } = groups[k];
                  const sorted = items.sort(byStartDesc);
                  return (
                    <section key={k} className="agenda-day">
                      <h4 className="agenda-heading">{formatDay(date)}</h4>
                      <ul className="agenda-list">
                        {sorted.map((ev, idx2) => (
                          <AgendaItem key={`${ev.id}-${ev._seqIndex}`} ev={ev} data={data} index={idx2} />
                        ))}
                      </ul>
                    </section>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
      <Pager />
    </>
  );
};

/* ---------- main ---------- */
const View = ({ data = {}, id }) => {
  const dispatch = useDispatch();
  const [processedEvents, setProcessedEvents] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingCount, setProcessingCount] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [batchNumber, setBatchNumber] = useState(0);
  const [allRawEvents, setAllRawEvents] = useState([]);
  const [hasMoreBatches, setHasMoreBatches] = useState(true);
  const [lastProcessedCount, setLastProcessedCount] = useState(0);

  const mode = data.mode || 'list';
  const limit = parseInt(data.limit, 10) || 0;

  const pathStr = useMemo(() => {
    const raw = typeof data.navPath === 'string' ? data.navPath.trim() : '';
    return raw ? ensureLeadingSlash(raw) : '';
  }, [data.navPath]);

  const tagsFilter = useMemo(() => {
    const raw = typeof data.tagFilter === 'string' ? data.tagFilter.trim() : '';
    if (!raw) return null;
    const arr = raw.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
    return arr.length ? arr : null;
  }, [data.tagFilter]);

  const portalTypes = useMemo(() => ['Event'], []);
  const BATCH_SIZE = 100; // Match what server seems to be returning
  const MAX_BATCHES = 50;

  const baseQuery = useMemo(
    () => ({
      portal_type: portalTypes,
      b_size: BATCH_SIZE,
      b_start: batchNumber * BATCH_SIZE,
      sort_on: 'start',
      sort_order: 'descending',
      fullobjects: false,
      metadata_fields: [
        'title', 'description', 'effective', 'start', 'end', 'location', 'Subject', 'contact_name',
        'recurrence', 'rrule', 'occurrences', '_recurrence', '_occurrences', 'RRULE'
      ],
      ...(pathStr ? { path: pathStr } : {}),
      ...(tagsFilter ? { Subject: tagsFilter } : {}),
      ...(data.review_state?.length ? { review_state: data.review_state } : {}),
    }),
    [portalTypes, batchNumber, pathStr, tagsFilter, data.review_state],
  );

  const queryKey = useMemo(() => JSON.stringify(baseQuery), [baseQuery]);
  const subkey = useMemo(() => `events-${id || 'block'}-batch-${batchNumber}`, [id, batchNumber]);

  const slice = useSelector((s) => s.search?.subrequests?.[subkey] || {});
  const raw = slice.items || [];
  const loading = !!slice.loading;
  const error = slice.error;
  const itemsTotal = slice.items_total || 0;

  // Fetch current batch
  useEffect(() => {
    if (hasMoreBatches) {
      const q = JSON.parse(queryKey);
      dispatch(searchContent('', q, subkey));
    }
  }, [dispatch, queryKey, subkey, hasMoreBatches]);

  // Accumulate raw events from all batches
  useEffect(() => {
    // Only process when we have new data that just finished loading
    if (raw.length === 0 || loading) return;
    
    console.log(`Batch ${batchNumber}: fetched ${raw.length} events`);
    
    setAllRawEvents(prev => {
      const combined = [...prev, ...raw];
      const unique = combined.filter((item, index, self) => 
        index === self.findIndex((t) => t['@id'] === item['@id'])
      );
      
      // Debug: log what years we're getting
      const years = {};
      unique.forEach(item => {
        const start = startDateOf(item);
        const year = start ? start.getFullYear() : 'unknown';
        years[year] = (years[year] || 0) + 1;
      });
      console.log('Year distribution:', years, 'Total:', unique.length);
      
      return unique;
    });
    
    // Safety check: don't fetch more than MAX_BATCHES
    if (batchNumber >= MAX_BATCHES - 1) {
      console.log('Reached maximum batch limit');
      setHasMoreBatches(false);
      return;
    }
    
    // Continue fetching if we got a full batch
    if (raw.length >= BATCH_SIZE) {
      console.log(`Full batch received, requesting batch ${batchNumber + 1}`);
      setTimeout(() => {
        setBatchNumber(prev => prev + 1);
      }, 50);
    } else {
      console.log(`Partial batch (${raw.length}/${BATCH_SIZE}) - stopping`);
      setHasMoreBatches(false);
    }
  }, [raw, loading]); // Only trigger on raw/loading changes

  // Progressive processing - process only NEW events as batches arrive
  useEffect(() => {
    if (allRawEvents.length === 0 || allRawEvents.length === lastProcessedCount) {
      return;
    }

    setIsProcessing(true);
    
    let cancelled = false;
    
    const processNewEvents = async () => {
      const now = new Date();
      const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      
      // Only process NEW events that haven't been processed yet
      const newRawEvents = allRawEvents.slice(lastProcessedCount);
      
      console.log(`Processing ${newRawEvents.length} new events (${lastProcessedCount} already processed)`);
      
      // For "all" mode, don't filter by date - show everything
      const relevantRaw = mode === 'all' 
        ? newRawEvents 
        : newRawEvents.filter(item => {
            const end = endDateOf(item);
            const start = startDateOf(item);
            
            if (end && end >= yesterdayStart) return true;
            if (item.recurrence || item.rrule || item.RRULE || 
                item.recurrence_rule || item.recurrenceRule) return true;
            if (start && start >= yesterdayStart) return true;
            
            return false;
          });
      
      const byYear = {};
      relevantRaw.forEach(item => {
        const start = startDateOf(item);
        const year = start ? start.getFullYear() : 9999;
        if (!byYear[year]) byYear[year] = [];
        byYear[year].push(item);
      });
      
      const years = Object.keys(byYear).map(Number).sort((a, b) => b - a);
      
      setTotalToProcess(prev => prev + relevantRaw.length);
      
      for (const year of years) {
        if (cancelled) break;
        
        const yearEvents = byYear[year];
        
        for (let i = 0; i < yearEvents.length; i++) {
          if (cancelled) break;
          
          setProcessingCount(prev => prev + 1);
          
          const expanded = await expandRecurrences([yearEvents[i]]);
          const mapped = expanded.map((it) => toEvent(it, data));
          
          setProcessedEvents(prev => [...prev, ...mapped]);
          
          await new Promise(resolve => setTimeout(resolve, 0));
        }
        
        if (years.indexOf(year) < years.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
      // Mark these events as processed
      setLastProcessedCount(allRawEvents.length);
      
      if (!cancelled && !hasMoreBatches) {
        setIsProcessing(false);
      }
    };
    
    processNewEvents();
    
    return () => {
      cancelled = true;
    };
  }, [allRawEvents, mode, data, hasMoreBatches, lastProcessedCount]);

  const baseSorted = useMemo(() => {
    if (mode === 'all') return [...processedEvents].sort(byStartDesc);
    const now = new Date();
    return processedEvents.filter((ev) => isUpcomingOrOngoing(ev, now)).sort(byStartAsc);
  }, [processedEvents, mode]);

  const limited =
    mode === 'all' ? baseSorted : limit > 0 ? baseSorted.slice(0, limit) : baseSorted;

  const hasEvents = baseSorted.length > 0;

  if (error)
    return (
      <div className={`events-block events-block--${mode}`}>
        <div className="events-empty">Error: {String(error)}</div>
      </div>
    );

  if (loading && allRawEvents.length === 0)
    return (
      <div className={`events-block events-block--${mode}`}>
        <div style={{ 
          padding: '12px 16px', 
          background: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '12px'
        }}>
          <div style={{ 
            height: '20px', 
            background: 'linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            marginBottom: '12px'
          }}></div>
          <div style={{ 
            height: '16px', 
            background: 'linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            width: '70%',
            marginBottom: '12px'
          }}></div>
          <div style={{ 
            height: '16px', 
            background: 'linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            width: '50%'
          }}></div>
        </div>
        <div style={{ 
          padding: '12px 16px', 
          background: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '12px'
        }}>
          <div style={{ 
            height: '20px', 
            background: 'linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            marginBottom: '12px'
          }}></div>
          <div style={{ 
            height: '16px', 
            background: 'linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px',
            width: '60%'
          }}></div>
        </div>
        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );

  return (
    <div className={`events-block events-block--${mode}`}>
      {hasEvents ? (
        <>
          {mode === 'list' && <List events={limited} data={data} />}
          {mode === 'agenda' && <Agenda events={limited} data={data} />}
          {mode === 'all' && (
            <AllEvents
              events={baseSorted}
              accordion={data.accordionByYear}
              pageSize={limit}
              data={data}
            />
          )}
        </>
      ) : !loading && !isProcessing && !hasMoreBatches ? (
        <div className="events-empty">No upcoming events found.</div>
      ) : null}
      
      {(isProcessing || hasMoreBatches) && (
        <div style={{ 
          padding: '8px 12px', 
          fontSize: '0.9em',
          color: '#666',
          background: '#f8f9fa', 
          border: '1px solid #e9ecef', 
          marginTop: '12px',
          borderRadius: '4px'
        }}>
          {hasMoreBatches && `Fetching batch ${batchNumber + 1}... (${allRawEvents.length} events loaded)`}
          {!hasMoreBatches && isProcessing && `Processing event ${processingCount} of ${totalToProcess}...`}
        </div>
      )}
    </div>
  );
};

export default View;